import { useRef, useState } from 'react';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatBytes } from '../../shared/lib/format';
import { Button } from '../../shared/ui/Button';
import { Card } from '../../shared/ui/primitives';
import { getArchiveJob, uploadArchive, uploadFile } from './api';
import type { ArchiveJob } from './types';

interface Props {
  examinationId: string;
  onChanged: () => void;
}

const POLL_MS = 1500;

type ItemState = 'queued' | 'uploading' | 'extracting' | 'done' | 'error';

/** Строка статуса загрузки: один файл или ZIP-архив. */
interface UploadItem {
  id: number;
  name: string;
  size: number;
  archive: boolean;
  state: ItemState;
  /** Доля отправленных байт (0..1) на этапе uploading. */
  progress: number;
  /** Для архива на этапе распаковки: «распаковано N из M». */
  note: string;
  error: string;
}

const stateLabel: Record<ItemState, string> = {
  queued: 'В очереди',
  uploading: 'Загрузка',
  extracting: 'Распаковка',
  done: 'Загружено',
  error: 'Ошибка',
};

const stateClass: Record<ItemState, string> = {
  queued: 'text-muted',
  uploading: 'text-accent-600',
  extracting: 'text-accent-600',
  done: 'text-success',
  error: 'text-danger',
};

let nextId = 1;

export function UploadPanel({ examinationId, onChanged }: Props) {
  const filesRef = useRef<HTMLInputElement>(null);
  const archiveRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<UploadItem[]>([]);

  function patch(id: number, p: Partial<UploadItem>) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...p } : it)));
  }

  function enqueue(files: File[], archive: boolean): UploadItem[] {
    const added = files.map<UploadItem>((f) => ({
      id: nextId++,
      name: f.name,
      size: f.size,
      archive,
      state: 'queued',
      progress: 0,
      note: '',
      error: '',
    }));
    // Завершённые строки прошлых загрузок убираем, ошибки оставляем.
    setItems((prev) => [...prev.filter((it) => it.state === 'error'), ...added]);
    return added;
  }

  async function handleFiles(list: FileList) {
    const files = Array.from(list);
    const queued = enqueue(files, false);
    setBusy(true);
    // Ошибка одного файла не останавливает остальные.
    for (let i = 0; i < files.length; i += 1) {
      const id = queued[i].id;
      patch(id, { state: 'uploading' });
      try {
        await uploadFile(examinationId, files[i], (progress) => patch(id, { progress }));
        patch(id, { state: 'done', progress: 1 });
        onChanged();
      } catch (e) {
        patch(id, { state: 'error', error: apiErrorMessage(e, 'Не удалось загрузить файл') });
      }
    }
    setBusy(false);
    if (filesRef.current) filesRef.current.value = '';
  }

  async function pollJob(itemId: number, jobId: string): Promise<ArchiveJob> {
    for (;;) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      const next = await getArchiveJob(examinationId, jobId);
      patch(itemId, { note: archiveNote(next) });
      onChanged();
      if (next.status === 'succeeded' || next.status === 'failed') return next;
    }
  }

  async function handleArchive(file: File) {
    const [item] = enqueue([file], true);
    setBusy(true);
    patch(item.id, { state: 'uploading' });
    try {
      const started = await uploadArchive(examinationId, file, (progress) =>
        patch(item.id, { progress }),
      );
      patch(item.id, { state: 'extracting', progress: 1, note: archiveNote(started) });
      const done = await pollJob(item.id, started.id);
      if (done.status === 'failed') {
        patch(item.id, { state: 'error', error: done.lastError || 'Не удалось распаковать архив' });
      } else {
        patch(item.id, { state: 'done' });
      }
    } catch (e) {
      patch(item.id, { state: 'error', error: apiErrorMessage(e, 'Не удалось загрузить архив') });
    } finally {
      setBusy(false);
      if (archiveRef.current) archiveRef.current.value = '';
    }
  }

  const doneCount = items.filter((it) => it.state === 'done').length;
  const errorCount = items.filter((it) => it.state === 'error').length;

  return (
    <Card className="space-y-3 p-4 sm:p-5">
      <h2 className="text-sm font-semibold text-ink">Загрузка файлов</h2>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          ref={filesRef}
          type="file"
          multiple
          disabled={busy}
          className="hidden"
          onChange={(e) =>
            e.target.files && e.target.files.length > 0 && handleFiles(e.target.files)
          }
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => filesRef.current?.click()}
        >
          Выбрать файлы
        </Button>

        <input
          ref={archiveRef}
          type="file"
          accept=".zip,application/zip"
          disabled={busy}
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleArchive(e.target.files[0])}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => archiveRef.current?.click()}
        >
          Загрузить ZIP-архив
        </Button>
      </div>

      {items.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
            <span>
              Готово {doneCount} из {items.length}
              {errorCount > 0 && <span className="text-danger"> · ошибок: {errorCount}</span>}
            </span>
            {!busy && (
              <button
                type="button"
                className="font-medium text-accent hover:text-accent-600"
                onClick={() => setItems([])}
              >
                Очистить
              </button>
            )}
          </div>
          <ul className="divide-y divide-line rounded-lg border border-line">
            {items.map((it) => (
              <UploadRow key={it.id} item={it} />
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function UploadRow({ item }: { item: UploadItem }) {
  const pct = Math.round(item.progress * 100);
  const showBar = item.state === 'uploading' || item.state === 'extracting';
  return (
    <li className="space-y-1 px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-ink" title={item.name}>
          {item.archive && <span className="text-muted">ZIP · </span>}
          {item.name}
        </span>
        <span className="shrink-0 text-muted">{formatBytes(item.size)}</span>
        <span className={`w-28 shrink-0 text-right font-medium ${stateClass[item.state]}`}>
          {stateLabel[item.state]}
          {item.state === 'uploading' && ` ${pct}%`}
        </span>
      </div>
      {showBar && (
        <div className="h-1 overflow-hidden rounded-full bg-accent-050">
          <div
            className={`h-full bg-accent transition-[width] ${item.state === 'extracting' ? 'animate-pulse' : ''}`}
            style={{ width: `${item.state === 'extracting' ? 100 : pct}%` }}
          />
        </div>
      )}
      {item.state === 'extracting' && item.note && <p className="text-muted">{item.note}</p>}
      {item.state === 'error' && <p className="text-danger">{item.error}</p>}
    </li>
  );
}

function archiveNote(job: ArchiveJob): string {
  return job.fileCount > 0 ? `Распаковано ${job.extracted} из ${job.fileCount}` : '';
}
